import type { Move } from '../../../schemas/move';

/**
 * Mosse iniziali del Mago (manuale V1, rr. 4083-4134).
 *
 * - **4 mosse automatiche** (`type: 'starting'`): *Corpus Arcano*, *Mana*,
 *   *Lanciare Incantesimi Arcani*, *Magia Improvvisata*. Tutte concesse
 *   automaticamente al livello 1.
 * - **2 mosse di scelta** (`type: 'starting'`, `pickCount: 1` nel gruppo
 *   `startingChoiceGroups`): *Scudo Magico* e *Scuola per Giovani Maghi*.
 *   Il giocatore ne sceglie **una** alla creazione (manuale r. 4124:
 *   "Inoltre, scegli una delle seguenti mosse").
 *
 * **Nota su *Magia Improvvisata***: il manuale la lista come iniziale
 * automatica del Mago, ma con `Richiede: Lanciare Incantesimi (Arcani,
 * Divini o del Patrono)`. Il Mago soddisfa il prerequisito automaticamente
 * grazie a *Lanciare Incantesimi Arcani*; il prerequisito resta nella mossa
 * per documentare il caso multiclasse di altre casteggianti.
 *
 * **Nota su *Scudo Magico*** (mossa di scelta): stesso pattern — `Richiede:
 * Lanciare Incantesimi`. *Scuola per Giovani Maghi* invece NON ha
 * prerequisiti.
 */

const corpusArcano: Move = {
  id: 'mago.corpus-arcano',
  name: 'Corpus Arcano',
  type: 'starting',
  classId: 'mago',
  trigger: 'Quando sali di livello e aggiungi un incantesimo al tuo repertorio',
  description:
    "La tua mente è una vasta biblioteca di magia. Inizi il gioco con tre incantesimi di primo livello e tutti i trucchetti della lista del Mago. Ogni volta che ***sali di livello***, aggiungi al tuo *Corpus Arcano* un nuovo incantesimo dalla lista del Mago, oppure uno che hai improvvisato con successo più volte grazie a *Magia Improvvisata* (a discrezione del GM). Il livello dell'incantesimo aggiunto al *Corpus Arcano* deve essere pari o inferiore al tuo livello da Mago.",
  tags: [],
  // Manuale V1 (rev. 2026-05-16): *Corpus Arcano* richiede *Lanciare
  // Incantesimi Arcani*. A L1 sono entrambe `starting` auto del Mago e
  // vengono acquisite contestualmente — `arePrerequisitesSatisfied` è
  // applicato solo alle mosse `advanced` nel level-up (vedi
  // `services/levelUp.ts:211`), quindi il prerequisito non blocca il flow
  // di creazione PG. Il prerequisito è semanticamente coerente col
  // pattern «LIA = trigger del casting» (FI-38 Opz. C). In multiclasse:
  // un PG non-Mago che prende LIA via mossa multiclasse riceve Corpus
  // Arcano via bundle (LIA è anchor).
  prerequisites: ['mago.lanciare-incantesimi-arcani'],
  replaces: [],
  // FI-33 (D2): regola Mago «+1 spell ogni level-up» (manuale V1 r. 1758).
  // Trigger `on-each-level-up` → si applica ogni volta che il PG sale di
  // livello finché possiede questa mossa. Multiclasse-friendly: chi
  // ottiene Corpus Arcano via *Lanciare Incantesimi Arcani* (FI-17 D7)
  // eredita automaticamente la regola.
  grantsSpells: [
    {
      trigger: 'on-each-level-up',
      count: 1,
      levelRule: { kind: 'lte-character-level' },
      source: { kind: 'own-class' },
    },
  ],
};

const mana: Move = {
  id: 'mago.mana',
  name: 'Mana',
  type: 'starting',
  classId: 'mago',
  trigger:
    'Quando ti *accampi* e ripassi gli incantesimi che conosci, preghi la tua divinità o rinnovi i reciproci accordi con il tuo Patrono',
  description:
    'Al risveglio perdi tutte le prese di *Mana* che hai e ne prendi un numero pari al tuo più alto livello da Incantatore +1 (massimo 11).\n\nIl *Mana* è uno solo: se già vi attingi come Chierico o Warlock, non puoi prendere di nuovo questa mossa.',
  // **D7-bundle (FI-17 follow-up)**: tag `'mana'` data-driven per il
  // dedup cross-class nel bundle expansion (manuale V1 r. 1133: «Mana è
  // una riserva unica»). Vedi `services/bundle.ts::isManaMove`.
  tags: ['mana'],
  prerequisites: [],
  replaces: [],
};

const lanciareIncantesimiArcani: Move = {
  id: 'mago.lanciare-incantesimi-arcani',
  name: 'Lanciare Incantesimi Arcani',
  type: 'starting',
  classId: 'mago',
  trigger: 'Quando vuoi lanciare un incantesimo che fa parte del tuo *Corpus Arcano*',
  description:
    "Se si tratta di un trucchetto, non importa che tiri: lo lanci e basta. Altrimenti, se il tuo livello da Mago e le tue prese di *Mana* residue sono almeno pari al livello dell'incantesimo che vuoi lanciare, tira+**INT** (nota che le prese di *Mana* le stai solo rischiando, non spendendo!). Con un 10+, l'incantesimo viene lanciato con successo. Con 7-9, lo stesso, ma devi anche scegliere un'opzione:\n\n- attiri attenzioni indesiderate, ti esponi al pericolo o metti te stesso in difficoltà. Il GM ti dirà come;\n- l'incantesimo disturba il tessuto della realtà: prendi -1 continuato a *Lanciare Incantesimi Arcani* fino a quando non usi di nuovo la mossa *Mana*. Se scegli più volte quest'opzione, il malus si somma;\n- spendi prese di *Mana* pari al livello dell'incantesimo lanciato.\n\nRicorda che mantenere attivo un incantesimo con effetti continuati potrebbe causare una penalità ai tuoi tiri di *lanciare incantesimi*.\n\nSe prendi questa mossa con una mossa multiclasse, ottieni anche *Corpus Arcano* e *Mana*.",
  roll: {
    stat: 'intelligenza',
    on10plus: { text: "L'incantesimo viene lanciato con successo." },
    on7to9: {
      text: "Lanci comunque l'incantesimo, ma scegli un'opzione: attenzioni indesiderate / -1 continuato fino a prossimo Mana / spendi prese di Mana pari al livello dell'incantesimo.",
    },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
  // **FI-35 (D-FI35)**: spell-grant strutturato per il setup-L1 di
  // *Lanciare Incantesimi Arcani*. Prima di FI-35 questa logica viveva
  // ad-hoc in `SectionSpellSelection` (`inferMagoL1FromDraft`). Ora è
  // SSOT su `grantsSpells`, applicato unitariamente dal service
  // `applyMoveAcquisition`.
  //  - Grant 1: i 3 trucchetti fissi del Mago auto-aggiunti (manuale:
  //    «inoltre lanci sempre *Luce*, *Prestidigitazione* e *Servitore
  //    Invisibile* come trucchetti»)
  //  - Grant 2: il giocatore sceglie 3 spell L1 dalla lista del Mago
  //
  // In multiclasse via *Combattente Multiclasse* + *Lanciare Incantesimi
  // Arcani* (D7/FI-17), gli stessi effetti verranno triggerati
  // post-creazione tramite `applyMoveAcquisition` — niente codice
  // duplicato.
  grantsSpells: [
    {
      trigger: 'on-take',
      levelRule: {
        kind: 'fixed-list',
        spellIds: ['mago.luce', 'mago.prestidigitazione', 'mago.servitore-invisibile'],
      },
      source: { kind: 'own-class' },
    },
    {
      trigger: 'on-take',
      count: 3,
      levelRule: { kind: 'fixed', level: 1 },
      source: { kind: 'own-class' },
    },
  ],
  // **D7-bundle (FI-17 follow-up)**: bundle all-inclusive del Mago
  // (manuale V1 r. 1770, esempio Grug: «opta per *Lanciare Incantesimi
  // Arcani*, ottenendo in omaggio *Corpus Arcano* e *Mana*»).
  // Anchor del bundle è la mossa di casting (decisione D7-bundle-Q1).
  // Mana dedupato cross-class via tag `'mana'` (D7-bundle-Q2) se il PG
  // possiede già una `*.mana` di altra classe casteggiante.
  bundles: ['mago.corpus-arcano', 'mago.mana'],
};

const magiaImprovvisata: Move = {
  id: 'mago.magia-improvvisata',
  name: 'Magia Improvvisata',
  type: 'starting',
  classId: 'mago',
  trigger:
    "Quando hai qualche minuto di tranquillità per improvvisare un incantesimo (o quando lo improvvisi 'al volo' in pochi secondi)",
  description:
    "Spiega l'effetto che desideri (e i suoi limiti) e concorda con il GM il suo livello (0, 1, 3, 5, 7 o 9) prendendo spunto dagli incantesimi esistenti. Se il tuo livello da Incantatore è più basso di quello concordato, *non* ottieni l'effetto. Altrimenti, scegli un numero di condizioni pari a 1 più la metà del livello stabilito (arrotondando per eccesso). Puoi scegliere la stessa opzione più volte, a meno che non sia indicato diversamente:\n\n- spendi 1 presa di *Mana*;\n- subisci una Debilitazione a tua scelta (puoi scegliere questa opzione una volta per ogni Debilitazione che non hai ancora segnato);\n- impieghi alcune ore (puoi scegliere quest'opzione una sola volta);\n- attingi alla magia di un luogo di potere in cui sei (una sola volta);\n- ti fai aiutare da qualcuno (descrivi da chi e come);\n- sacrifichi un raro ingrediente o un oggetto magico (descrivi quale).\n\nSe il GM approva le tue scelte, ottieni l'effetto desiderato. Se l'effetto è continuato, subisci -1 a *lanciare incantesimi* finché resta attivo. Con questa mossa *non* puoi ottenere effetti permanenti.\n\nQuando **improvvisi un incantesimo 'al volo'**, ti basta qualche secondo. Individua il livello dell'effetto e scegli le condizioni, poi tira come per *lanciare incantesimi*. Con 10+, tutto funziona come previsto. Con 7-9, il GM ti dirà un effetto collaterale, un difetto intrinseco, una strana limitazione o come l'incantesimo ha turbato la trama stessa della magia.",
  tags: [],
  prerequisites: ['mago.lanciare-incantesimi-arcani'],
  replaces: [],
};

const scudoMagico: Move = {
  id: 'mago.scudo-magico',
  name: 'Scudo Magico',
  type: 'starting',
  classId: 'mago',
  trigger:
    'Quando plasmi la magia grezza per contrastare gli effetti di un incantesimo che sta per colpirti',
  description:
    "Spendi 1 presa di *Mana* e tira come per *lanciare incantesimi*. Con 10+, l'incantesimo ostile non ha effetto su di te. Con 7-9, lo stesso, ma attiri attenzioni indesiderate, ti esponi al pericolo o metti te stesso in difficoltà. Il GM ti dirà come.\n\n*Scudo Magico* protegge soltanto te: non puoi usarlo su altri bersagli.",
  tags: [],
  prerequisites: ['mago.lanciare-incantesimi-arcani'],
  replaces: [],
};

const scuolaPerGiovaniMaghi: Move = {
  id: 'mago.scuola-per-giovani-maghi',
  name: 'Scuola per Giovani Maghi',
  type: 'starting',
  classId: 'mago',
  trigger: 'Quando usi una Borsa di libri',
  description:
    "Ottieni +1 al prossimo tiro per una qualsiasi mossa, anche se non è *declamare conoscenze*. Se lo fai, spiega che genere di aiuto hai trovato sepolto tra le pagine.\n\nInoltre, ignori l'etichetta *lento* della tua Borsa di libri.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

/**
 * Mosse iniziali AUTOMATICHE (4): prese tutte al livello 1 senza scelta.
 */
export const magoStartingAutoMoves: readonly Move[] = [
  corpusArcano,
  mana,
  lanciareIncantesimiArcani,
  magiaImprovvisata,
] as const;

/**
 * Mosse iniziali di SCELTA (2): il giocatore ne sceglie UNA al livello 1
 * (`pickCount: 1`).
 */
export const magoStartingChoiceMoves: readonly Move[] = [
  scudoMagico,
  scuolaPerGiovaniMaghi,
] as const;

/** Tutte le mosse iniziali (auto + scelta). */
export const magoInitialMoves: readonly Move[] = [
  ...magoStartingAutoMoves,
  ...magoStartingChoiceMoves,
];
